import { ACCOUNT_PROVIDER_ID, type AccountInfo } from '@/composables/accounts/providers/types';
import { type Eip1193Provider, ethers, Contract } from 'ethers';
import AccountProviderInterface from '@/composables/accounts/providers/accountProviderInterface';
import { getContractConfig } from '@/common/predictionMarkets/predictionMarkets';
import { IS_MAINNET, BSC_MAINNET_NETWORK, BSC_TESTNET_NETWORK } from '@/common/config';
import { formatUnits, logWarn } from '@/common/helpers';

// Type-only imports (stripped at compile time, don't affect bundle size)
import type { AppKit } from '@reown/appkit';
import type { ProviderControllerState } from '@reown/appkit-controllers';

// Minimal ERC20 ABI for token interactions
const ERC20_ABI = ['function decimals() view returns (uint8)', 'function balanceOf(address) view returns (uint256)'];

// ERC20 Contract type with methods
type ERC20Contract = Contract & {
  decimals: () => Promise<bigint>;
  balanceOf: (address: string) => Promise<bigint>;
};

function isEip1193Provider(p: unknown): p is Eip1193Provider {
  return !!p && typeof p === 'object' && 'request' in p && typeof p.request === 'function';
}

/**
 * Minimal EVM account provider using Reown AppKit + Ethers v6
 * Reown packages (~607 KiB) are lazy-loaded in init() to reduce initial bundle size
 */
export class ReownAppKitEthersAccountProvider extends AccountProviderInterface {
  private modal: AppKit | null = null;
  // Subscription handle for provider state changes (currently auto-managed by AppKit)
  // @ts-expect-error - Variable assigned but not currently used (kept for future cleanup logic)
  private unsubscribeProviders: (() => void) | null = null;

  private onAccountChangeHandler: ((accountInfo: AccountInfo, providerId: ACCOUNT_PROVIDER_ID) => void) | null = null;

  // ethers v6
  private ethersProvider: ethers.BrowserProvider | null = null;
  // Store connected signer for social wallets (they don't support traditional account requests)
  private connectedSigner: ethers.JsonRpcSigner | null = null;

  async init(onAccountChangeHandler: (accountInfo: AccountInfo, providerId: ACCOUNT_PROVIDER_ID) => void): Promise<void> {
    this.onAccountChangeHandler = onAccountChangeHandler;

    // Create once
    if (!this.modal) {
      const projectId = import.meta.env.VITE_REOWN_PROJECT_ID as string;
      if (!projectId) throw new Error('Missing VITE_REOWN_PROJECT_ID');

      const metadata = {
        name: 'FLM Fun',
        description: 'Prediction markets for streamers and creators',
        url: window.location.origin,
        icons: ['https://flm.fun/apple-touch-icon.png'],
      };

      // Lazy load Reown packages (~607 KiB) only when user connects wallet
      const [{ createAppKit }, { EthersAdapter }] = await Promise.all([
        import('@reown/appkit/vue'),
        import('@reown/appkit-adapter-ethers'),
      ]);

      const targetNetwork = IS_MAINNET ? BSC_MAINNET_NETWORK : BSC_TESTNET_NETWORK;

      this.modal = createAppKit({
        adapters: [new EthersAdapter()],
        networks: [BSC_MAINNET_NETWORK, BSC_TESTNET_NETWORK],
        defaultNetwork: targetNetwork, // Required for social logins to know which RPC to use
        // Reown enforces network automatically with allowUnsupportedChain: false
        // Users can only interact with BSC networks (mainnet or testnet)
        enableNetworkSwitch: true,
        allowUnsupportedChain: false,
        projectId,
        metadata,
        features: {
          analytics: false,
          email: true, // Enable email/social login
          socials: ['google', 'x', 'discord', 'apple'], // Enable social auth providers
          onramp: true, // Enable on-ramp with default network support
        },
        themeMode: 'dark',
        themeVariables: {
          '--w3m-accent': '#3b82f6',
        },
      });
    }

    this.unsubscribeProviders = this.modal.subscribeProviders(async (state: ProviderControllerState['providers']) => {
      const eip155Provider = state.eip155;
      const address = this.modal?.getAddress?.() ?? this.modal?.getAccount?.()?.address;
      const chainId = this.modal?.getChainId?.() ?? this.modal?.getCaipNetwork?.()?.id;
      const isConnected = this.modal?.getIsConnectedState?.() ?? Boolean(address);

      if (isEip1193Provider(eip155Provider)) {
        this.ethersProvider = new ethers.BrowserProvider(eip155Provider);

        // Don't immediately cache signer here - social login flow may not be complete
        // Will be cached lazily in getAccountSigner() when first needed
        if (!address || !isConnected) {
          this.connectedSigner = null;
        }
      } else {
        this.ethersProvider = null;
        this.connectedSigner = null;
      }

      if (this.onAccountChangeHandler) {
        // No address during disconnect is expected behavior - silently skip
        if (!address || !isConnected) {
          return;
        }

        const accountInfo: AccountInfo = {
          id: address && chainId ? `${chainId}:${address}` : '',
          address,
          name: address,
        };

        this.onAccountChangeHandler(accountInfo, ACCOUNT_PROVIDER_ID.REOWN_EVM_ACCOUNT_PROVIDER);
      }
    });
  }

  async connect(): Promise<void> {
    if (!this.modal) throw new Error('Provider not initialized. Call init() first.');

    // Check if we're in a mobile wallet's in-app browser (has injected provider)
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const hasInjectedProvider = typeof window !== 'undefined' && window.ethereum;

    // On mobile with injected provider (e.g., MetaMask mobile browser),
    // connect directly without Reown modal (Reown is for WalletConnect only)
    if (isMobile && hasInjectedProvider && isEip1193Provider(window.ethereum)) {
      return this.connectDirectToInjectedProvider();
    }

    // Desktop or mobile without injected provider: use Reown modal
    // Pre-add target network only on testnet (mainnet BSC is already known by all wallets)
    if (!IS_MAINNET) await this.ensureNetworkAdded();

    await this.modal.open({ view: 'Connect', namespace: 'eip155' });
  }

  /**
   * Connect directly to injected provider (e.g., MetaMask mobile browser)
   * without going through Reown modal
   */
  private async connectDirectToInjectedProvider(): Promise<void> {
    if (!window.ethereum || !isEip1193Provider(window.ethereum)) {
      throw new Error('No injected provider found');
    }

    const ethereum = window.ethereum;

    // Add BSC network if not present (testnet only - mainnet is already known by all wallets)
    if (!IS_MAINNET) await this.ensureNetworkAdded();

    // Request account access
    const accounts = (await ethereum.request({ method: 'eth_requestAccounts' })) as string[];
    if (!accounts || accounts.length === 0) {
      throw new Error('No accounts returned');
    }

    const chainId = (await ethereum.request({ method: 'eth_chainId' })) as string;
    const address = accounts[0]!;

    // Create ethers provider
    this.ethersProvider = new ethers.BrowserProvider(ethereum);

    // Don't cache signer here - will be lazily cached in getAccountSigner() when needed

    // Set up event listeners for account/network changes
    if ('on' in ethereum && typeof ethereum.on === 'function') {
      ethereum.on('accountsChanged', async (newAccounts: string[]) => {
        if (newAccounts.length === 0 || !this.onAccountChangeHandler) {
          // Account disconnected
          this.connectedSigner = null;
          return;
        }

        // Clear cached signer (will be re-cached lazily when needed)
        this.connectedSigner = null;

        const accountInfo: AccountInfo = {
          id: `${parseInt(chainId, 16)}:${newAccounts[0]!}`,
          address: newAccounts[0]!,
          name: newAccounts[0]!,
        };
        this.onAccountChangeHandler(accountInfo, ACCOUNT_PROVIDER_ID.REOWN_EVM_ACCOUNT_PROVIDER);
      });

      ethereum.on('chainChanged', () => {
        // Reload page on network change (standard practice)
        window.location.reload();
      });
    }

    // Trigger account change handler
    if (this.onAccountChangeHandler) {
      const accountInfo: AccountInfo = {
        id: `${parseInt(chainId, 16)}:${address}`,
        address,
        name: address,
      };
      this.onAccountChangeHandler(accountInfo, ACCOUNT_PROVIDER_ID.REOWN_EVM_ACCOUNT_PROVIDER);
    }
  }

  /**
   * Pre-adds the target BSC network to the wallet using EIP-3085.
   *
   * This prevents Reown's EthersAdapter from failing during connection when it
   * attempts to switch to the configured network. If the chain already exists or
   * the user rejects, we continue silently - the connection will proceed.
   *
   * @private
   */
  private async ensureNetworkAdded(): Promise<void> {
    if (typeof window === 'undefined' || !window.ethereum) {
      return;
    }

    // Type guard to ensure ethereum is an EIP-1193 provider
    const ethereum = window.ethereum;
    if (!isEip1193Provider(ethereum)) {
      return;
    }

    const targetNetwork = IS_MAINNET ? BSC_MAINNET_NETWORK : BSC_TESTNET_NETWORK;

    try {
      // Build a fresh plain object - Reown may mutate/proxy the BSC_*_NETWORK
      // objects after createAppKit(), making them non-cloneable via postMessage
      await ethereum.request({
        method: 'wallet_addEthereumChain',
        params: [
          {
            chainId: `0x${targetNetwork.id.toString(16)}`,
            chainName: String(targetNetwork.name),
            nativeCurrency: {
              name: String(targetNetwork.nativeCurrency.name),
              symbol: String(targetNetwork.nativeCurrency.symbol),
              decimals: Number(targetNetwork.nativeCurrency.decimals),
            },
            rpcUrls: [String(targetNetwork.rpcUrls.default.http[0])],
            blockExplorerUrls: [String(targetNetwork.blockExplorers.default.url)],
          },
        ],
      });
    } catch (error) {
      // Expected: chain exists or user rejected - both are fine
      logWarn('ReownProvider', 'Network pre-add skipped (expected if chain exists)', error);
    }
  }

  /**
   * Ensures the user is on the correct network before executing blockchain transactions.
   *
   * Call this at the beginning of ANY blockchain interaction (contract calls, token transfers, etc.)
   * to verify the user is on the correct BSC network (mainnet or testnet based on environment).
   *
   * If on wrong network, prompts user to switch. Throws if user rejects or switching fails.
   *
   * @throws {Error} If user rejects network switch or if no wallet is connected
   *
   * @example
   * async function createMarket() {
   *   await accountProvider.ensureCorrectNetwork();
   *   // Now safe to execute contract call
   *   await contract.createMarket(...);
   * }
   */
  async ensureCorrectNetwork(): Promise<void> {
    if (!this.modal) {
      throw new Error('Wallet not connected - cannot verify network');
    }

    const currentChainId = this.modal.getChainId?.() ?? this.modal.getCaipNetwork?.()?.id;
    const targetNetwork = IS_MAINNET ? BSC_MAINNET_NETWORK : BSC_TESTNET_NETWORK;

    // Already on correct network
    if (currentChainId === targetNetwork.id) {
      return;
    }

    // Wrong network - attempt to switch using Reown's switchNetwork
    // This will prompt the user via their wallet
    try {
      await this.modal.switchNetwork(targetNetwork);
    } catch {
      throw new Error(`Must switch to ${targetNetwork.name} (chain ID ${targetNetwork.id}) to continue. Current chain: ${currentChainId}`);
    }
  }

  async disconnect(): Promise<void> {
    if (!this.modal) return;

    await this.modal.disconnect('eip155');

    // Clear local state immediately
    this.ethersProvider = null;
    this.connectedSigner = null;
  }

  /**
   * Opens the Reown account modal for account management (view profile, disconnect, etc.)
   * Only available for Reown accounts
   */
  async openAccountSettings(): Promise<void> {
    if (!this.modal) return;
    await this.modal.open();
  }

  async getAccountBalance(accountId: string): Promise<string> {
    if (!this.ethersProvider) throw new Error('Wallet not connected');

    // Get token address dynamically from contract config
    const config = await getContractConfig();
    const tokenAddress = config.predictionCurrency;

    const erc20 = new Contract(tokenAddress, ERC20_ABI, this.ethersProvider) as ERC20Contract;

    const raw = await erc20.balanceOf(accountId);
    const decimals = await erc20.decimals();

    return formatUnits(raw, Number(decimals));
  }

  async getAccountInfo(accountId: string): Promise<AccountInfo> {
    if (!this.modal) throw new Error('Provider not initialized');

    const address = this.modal?.getAddress?.() ?? accountId;
    const chainId = this.modal?.getChainId?.() ?? 0;
    const id = `${chainId}:${address}`;

    return {
      id,
      address,
      name: address,
    };
  }

  async getAccountSigner(accountId: string): Promise<ethers.Signer> {
    if (!this.ethersProvider) throw new Error('Wallet not connected');
    if (!this.modal) throw new Error('Provider not initialized');

    // If we have a cached signer, use it
    if (this.connectedSigner) {
      try {
        const signerAddress = await this.connectedSigner.getAddress();
        if (signerAddress.toLowerCase() === accountId.toLowerCase()) {
          return this.connectedSigner;
        }
      } catch {
        // Cached signer is stale, clear it
        this.connectedSigner = null;
      }
    }

    // For social wallets: wrap the provider to intercept eth_requestAccounts and eth_accounts
    // since social wallets don't support these calls (they're already authenticated)
    const walletProvider = await this.modal.getWalletProvider();

    if (walletProvider && isEip1193Provider(walletProvider)) {
      // Create a proxy that intercepts account-related calls
      const wrappedProvider = new Proxy(walletProvider, {
        get(target, prop) {
          if (prop === 'request') {
            return async (args: { method: string; params?: unknown[] }) => {
              // Intercept eth_requestAccounts - return the current address instead of requesting
              if (args.method === 'eth_requestAccounts') {
                return [accountId];
              }
              // Intercept eth_accounts - return the current address
              if (args.method === 'eth_accounts') {
                return [accountId];
              }
              // Pass through all other requests
              return target.request(args);
            };
          }
          return target[prop as keyof typeof target];
        },
      });

      try {
        // Create provider and get signer
        const provider = new ethers.BrowserProvider(wrappedProvider as Eip1193Provider);
        const signer = await provider.getSigner(accountId);
        const address = await signer.getAddress();

        if (address.toLowerCase() === accountId.toLowerCase()) {
          this.connectedSigner = signer;
          return signer;
        }
      } catch {
        // If that fails, try without specifying the address
        try {
          const provider = new ethers.BrowserProvider(wrappedProvider as Eip1193Provider);
          const signer = await provider.getSigner();
          this.connectedSigner = signer;
          return signer;
        } catch {
          // Continue to fallback
        }
      }
    }

    // Fallback: Try traditional approaches for non-Reown wallets
    try {
      const addresses = await this.ethersProvider.listAccounts();

      if (addresses.length > 0) {
        const matchingIndex = addresses.findIndex((addr) => addr.address.toLowerCase() === accountId.toLowerCase());

        if (matchingIndex !== -1) {
          const signer = addresses[matchingIndex] as ethers.JsonRpcSigner;
          this.connectedSigner = signer;
          return signer;
        }
      }
    } catch {
      // listAccounts failed, continue
    }

    throw new Error('Unable to get signer. Please reconnect your wallet.');
  }
}

export default ReownAppKitEthersAccountProvider;
